import { api } from './api';
import type { App } from './apps';

const PREFIX = '/admin';

/** Admin app reuses the launcher App interface (same shape). */
export type AdminApp = App;

/** Per-app role for admin (e.g. viewer, trader). */
export interface AdminAppRole {
  id: string;
  app_id: string;
  name: string;
  permissions: string[];
  is_default: boolean;
  display_order: number;
}

/** User-app access grant (admin view). */
export interface UserAppAccessGrant {
  id: string;
  user_id: string;
  app_id: string;
  app_name: string | null;
  app_route_path: string | null;
  app_role: { id: string; name: string } | null;
  created_at: string;
  updated_at: string;
}

/** App-scoped user access (for app admin: who has access + their role). */
export interface AppUserAccessItem {
  id: string;
  user_id: string;
  user_email: string | null;
  app_role: { id: string; name: string } | null;
  created_at: string;
  updated_at: string;
}

export interface AllowedUser {
  id: string;
  email: string;
  google_sub: string | null;
  active: boolean;
  role_ids: string[];
  created_at: string;
  updated_at: string;
}

export interface Role {
  id: string;
  name: string;
  permission_ids: string[];
  created_at: string;
  updated_at: string;
}

export interface Permission {
  id: string;
  identifier: string;
  created_at: string;
}

export const adminApi = {
  async listUsers(includeInactive = false): Promise<AllowedUser[]> {
    const q = includeInactive ? '?include_inactive=true' : '';
    return api.get<AllowedUser[]>(`${PREFIX}/users${q}`);
  },

  async addUser(email: string, roleIds: string[]): Promise<AllowedUser> {
    return api.post<AllowedUser>(`${PREFIX}/users`, { email, role_ids: roleIds });
  },

  async getUser(id: string): Promise<AllowedUser> {
    return api.get<AllowedUser>(`${PREFIX}/users/${id}`);
  },

  async updateUserRoles(id: string, roleIds: string[]): Promise<AllowedUser> {
    return api.patch<AllowedUser>(`${PREFIX}/users/${id}`, { role_ids: roleIds });
  },

  async deleteUser(id: string): Promise<void> {
    return api.delete(`${PREFIX}/users/${id}`);
  },

  async listRoles(): Promise<Role[]> {
    return api.get<Role[]>(`${PREFIX}/roles`);
  },

  async listPermissions(): Promise<Permission[]> {
    return api.get<Permission[]>(`${PREFIX}/permissions`);
  },

  async createPermission(identifier: string): Promise<Permission> {
    return api.post<Permission>(`${PREFIX}/permissions`, { identifier });
  },

  async createRole(name: string, permissionIds: string[]): Promise<Role> {
    return api.post<Role>(`${PREFIX}/roles`, { name, permission_ids: permissionIds });
  },

  async getRole(id: string): Promise<Role> {
    return api.get<Role>(`${PREFIX}/roles/${id}`);
  },

  async updateRole(
    id: string,
    payload: { name?: string; permission_ids?: string[] }
  ): Promise<Role> {
    return api.patch<Role>(`${PREFIX}/roles/${id}`, payload);
  },

  async deleteRole(id: string): Promise<void> {
    return api.delete(`${PREFIX}/roles/${id}`);
  },

  // Apps (admin registry)
  async listApps(): Promise<AdminApp[]> {
    return api.get<AdminApp[]>(`${PREFIX}/apps`);
  },

  async getApp(id: string): Promise<AdminApp> {
    return api.get<AdminApp>(`${PREFIX}/apps/${id}`);
  },

  async createApp(payload: {
    name: string;
    route_path: string;
    description?: string | null;
    icon?: string | null;
    status?: string;
    display_order?: number;
    theme_id?: string | null;
  }): Promise<AdminApp> {
    return api.post<AdminApp>(`${PREFIX}/apps`, payload);
  },

  async updateApp(
    id: string,
    payload: {
      name?: string;
      route_path?: string;
      description?: string | null;
      icon?: string | null;
      status?: string;
      display_order?: number;
      theme_id?: string | null;
    }
  ): Promise<AdminApp> {
    return api.put<AdminApp>(`${PREFIX}/apps/${id}`, payload);
  },

  async listAppRoles(appId: string): Promise<AdminAppRole[]> {
    return api.get<AdminAppRole[]>(`${PREFIX}/apps/${appId}/roles`);
  },

  async listAppUserAccess(appId: string): Promise<AppUserAccessItem[]> {
    return api.get<AppUserAccessItem[]>(`${PREFIX}/apps/${appId}/user-access`);
  },

  // User-app access
  async listUserAppAccess(userId: string): Promise<UserAppAccessGrant[]> {
    return api.get<UserAppAccessGrant[]>(`${PREFIX}/users/${userId}/app-access`);
  },

  async grantUserAppAccess(userId: string, appId: string): Promise<UserAppAccessGrant> {
    return api.post<UserAppAccessGrant>(`${PREFIX}/users/${userId}/app-access`, {
      app_id: appId,
    });
  },

  async patchUserAppAccess(
    userId: string,
    accessId: string,
    appRoleId: string
  ): Promise<UserAppAccessGrant> {
    return api.patch<UserAppAccessGrant>(`${PREFIX}/users/${userId}/app-access/${accessId}`, {
      app_role_id: appRoleId,
    });
  },

  async revokeUserAppAccess(userId: string, accessId: string): Promise<void> {
    return api.delete(`${PREFIX}/users/${userId}/app-access/${accessId}`);
  },
};
